/**
 * notification-center.tsx — Bộ điều phối thông báo tin nhắn phía trình duyệt.
 * Gắn MỘT lần trong AppLayout; không vẽ gì.
 *
 * Nghe socket (phòng riêng user:{id}, máy chủ đã lọc người nhận theo cài đặt tổ chức):
 *   notify:message   { conversationId, contactName, preview, category, … }
 *   notify:reminder  { conversationId, contactName, waitingMinutes }
 *   notification:new → làm mới chuông thông báo
 *
 * Rồi theo tuỳ chọn cá nhân (/account/notifications) quyết định qua decideNotify():
 * đổ chuông (chống dồn 2s, lặp theo chu kỳ tới khi quay lại tab), thông báo
 * trình duyệt (bấm → về tab + mở hội thoại), nháy tiêu đề tab khi tab ẩn.
 */
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { getSocket } from '@/lib/socket'
import { useAuthStore } from '@/stores/auth-store'
import { usePresenceMap } from '@/hooks/use-presence'
import { useMyNotifyPrefs } from '@/hooks/use-notification-settings'
import {
  DEFAULT_USER_NOTIFY, decideNotify, flashTitle, openConversationFromPath,
  type DesktopPermission, type NotifyContext, type NotifyEvent,
} from '@/lib/notify/notify-decision'
import { installAudioUnlock, playNotifySound, preloadNotifySounds } from '@/lib/notify/notify-sound'
import { enablePush } from '@/lib/web-push'
import { setAppBadgeCount } from '@/lib/app-badge'
import { useConversationCounts } from '@/hooks/use-conversations'
import { OPEN_URL_MESSAGE, conversationUrl, safeInAppUrl, showSystemNotification } from '@/lib/notify/system-notification'

interface MessagePayload {
  conversationId: string
  contactName?: string
  preview?: string
  /** Tiêu đề + nội dung đã ghép sẵn ở máy chủ (nhóm: tên nhóm / "Người gửi: nội dung"). */
  title?: string
  body?: string
  senderUserId?: string | null
  isInternal?: boolean
}
interface ReminderPayload {
  conversationId: string
  contactName?: string
  waitingMinutes?: number
}

export function desktopPermission(): DesktopPermission {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported'
  return Notification.permission as DesktopPermission
}

const tabActive = () => document.visibilityState === 'visible' && document.hasFocus()

export function NotificationCenter() {
  const { t } = useTranslation('sharedMisc')
  const user = useAuthStore((s) => s.user)
  const userId = user?.id
  const { data: prefs } = useMyNotifyPrefs(!!userId)
  const { data: presence } = usePresenceMap(!!userId)
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const qc = useQueryClient()

  // Giá trị mới nhất cho các handler socket (đăng ký một lần).
  const live = useRef({ userId, prefs: prefs ?? DEFAULT_USER_NOTIFY, presence, pathname, navigate })
  live.current = { userId, prefs: prefs ?? DEFAULT_USER_NOTIFY, presence, pathname, navigate }

  const unread = useRef(0)
  const flashTimer = useRef<ReturnType<typeof setInterval> | null>(null)
  const baseTitle = useRef<string | null>(null)
  const repeatTimer = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    preloadNotifySounds()
    return installAudioUnlock()
  }, [])

  // Số tin chưa đọc trên biểu tượng ứng dụng đã cài (dùng chung truy vấn đếm với thanh tab / Hộp thư); đăng xuất → xoá.
  const { data: counts } = useConversationCounts()
  const unreadBadge = userId ? counts?.unread : 0
  useEffect(() => { void setAppBadgeCount(unreadBadge) }, [unreadBadge])

  // Đã bật "Thông báo trình duyệt" + trình duyệt đã cấp quyền → đăng ký (hoặc làm mới) thiết bị này nhận thông báo đẩy.
  // Chạy mỗi lần mở ứng dụng: sửa được trường hợp trình duyệt đổi địa chỉ đăng ký, hoặc thiết bị mới của người đã bật.
  const desktopOn = !!prefs?.desktop
  useEffect(() => {
    if (!userId || !desktopOn || desktopPermission() !== 'granted') return
    void enablePush()
  }, [userId, desktopOn])

  // Bấm thông báo hệ điều hành (do service worker hiện) → sw.js đưa cửa sổ này lên trước và gửi đường dẫn cần mở.
  useEffect(() => {
    const sw = typeof navigator !== 'undefined' ? navigator.serviceWorker : undefined
    if (!sw) return
    const onMessage = (e: MessageEvent) => {
      if (e.data?.type !== OPEN_URL_MESSAGE) return
      const url = safeInAppUrl(e.data.url)
      if (url) live.current.navigate(url)
    }
    sw.addEventListener('message', onMessage)
    return () => sw.removeEventListener('message', onMessage)
  }, [])

  useEffect(() => {
    if (!userId) return
    const socket = getSocket()

    const stopFlash = () => {
      if (flashTimer.current) clearInterval(flashTimer.current)
      flashTimer.current = null
      if (baseTitle.current !== null) document.title = baseTitle.current
      baseTitle.current = null
      unread.current = 0
    }
    const startFlash = () => {
      unread.current += 1
      if (flashTimer.current) return
      baseTitle.current = document.title
      let tick = 0
      document.title = flashTitle(baseTitle.current, unread.current, tick)
      flashTimer.current = setInterval(() => {
        tick += 1
        document.title = flashTitle(baseTitle.current ?? '', unread.current, tick)
      }, 1000)
    }

    const stopRepeat = () => {
      if (repeatTimer.current) clearInterval(repeatTimer.current)
      repeatTimer.current = null
    }
    const startRepeat = () => {
      stopRepeat()
      const { repeat } = live.current.prefs
      let played = 1 // lần đầu đã phát
      repeatTimer.current = setInterval(() => {
        const p = live.current.prefs
        if (tabActive() || played >= p.repeat.maxTimes || !p.sound.enabled) return stopRepeat()
        played += 1
        void playNotifySound(p.sound.tone, p.sound.volume, true)
      }, repeat.intervalSec * 1000)
    }

    const showDesktop = (title: string, body: string, conversationId: string) => {
      void showSystemNotification(title, {
        body,
        tag: `conv:${conversationId}`,
        url: conversationUrl(conversationId),
        onClick: () => live.current.navigate(conversationUrl(conversationId)),
      })
    }

    const ctx = (): NotifyContext => {
      const l = live.current
      return {
        myUserId: l.userId,
        myPresence: l.userId ? l.presence?.[l.userId] : undefined,
        prefs: l.prefs,
        tabVisible: document.visibilityState === 'visible',
        tabFocused: document.hasFocus(),
        openConversationId: openConversationFromPath(l.pathname),
        desktopPermission: desktopPermission(),
      }
    }

    const handle = (ev: NotifyEvent, title: string, body: string) => {
      const d = decideNotify(ev, ctx())
      const p = live.current.prefs
      if (d.sound) void playNotifySound(p.sound.tone, p.sound.volume)
      if (d.repeat) startRepeat()
      if (d.flash) startFlash()
      if (d.desktop) showDesktop(title, body, ev.conversationId)
    }

    const onMessage = (m: MessagePayload) => {
      if (!m?.conversationId) return
      const name = m.contactName || t('notificationCenter.fallbackContactName')
      handle(
        { kind: 'message', conversationId: m.conversationId, senderUserId: m.senderUserId, isInternal: m.isInternal },
        m.title || name,
        m.body || m.preview || t('notificationCenter.fallbackMessageBody'),
      )
    }

    const onReminder = (r: ReminderPayload) => {
      if (!r?.conversationId) return
      qc.invalidateQueries({ queryKey: ['notifications'] })
      const name = r.contactName || t('notificationCenter.fallbackContactName')
      const body = t('notificationCenter.reminderBody', { name, minutes: r.waitingMinutes ?? '?' })
      const title = t('notificationCenter.reminderTitle', { name })
      toast.warning(title, {
        description: body,
        action: { label: t('notificationCenter.open'), onClick: () => live.current.navigate(`/conversations/${r.conversationId}`) },
      })
      handle({ kind: 'reminder', conversationId: r.conversationId }, title, body)
    }

    const onBell = (n?: { type?: string; title?: string; body?: string; link?: string | null }) => {
      qc.invalidateQueries({ queryKey: ['notifications'] })
      if (n?.type !== 'billing_paid') return
      // Đơn vừa thanh toán xong (kể cả trả ở app ngân hàng / máy khác): làm mới gói + lịch sử + thanh nhắc, báo ở mọi menu.
      for (const k of ['billing-orders', 'billing-order', 'billing-summary']) qc.invalidateQueries({ queryKey: [k] })
      qc.invalidateQueries({ queryKey: ['ai', 'credits'] })
      // Các trang /billing (kể cả trang kết quả) tự báo bằng khung theo dõi đơn — không báo trùng.
      if (live.current.pathname.startsWith('/billing')) return
      toast.success(n.title || t('notificationCenter.paymentSuccessFallback'), {
        description: n.body,
        duration: 10_000,
        action: { label: t('notificationCenter.view'), onClick: () => live.current.navigate(n.link?.startsWith('/billing') ? n.link : '/billing') },
      })
    }

    const onActive = () => {
      if (!tabActive()) return
      stopFlash()
      stopRepeat()
    }

    socket.on('notify:message', onMessage)
    socket.on('notify:reminder', onReminder)
    socket.on('notification:new', onBell)
    window.addEventListener('focus', onActive)
    document.addEventListener('visibilitychange', onActive)
    return () => {
      socket.off('notify:message', onMessage)
      socket.off('notify:reminder', onReminder)
      socket.off('notification:new', onBell)
      window.removeEventListener('focus', onActive)
      document.removeEventListener('visibilitychange', onActive)
      stopFlash()
      stopRepeat()
    }
  }, [qc, userId, t])

  return null
}
